import { Show, type ParentComponent } from "solid-js"
import { Navigate, useLocation } from "@solidjs/router"
import { AppLoading } from "@/components/app-loading"
import { authConnectionLoadingMessage } from "./connection-issue"
import { useAuth } from "./index"
import { storeReturnTo } from "./storage"

function AuthLoading(props: { message: string }) {
  return <AppLoading message={props.message} />
}

export const AuthGuard: ParentComponent<{
  allowPendingDesktopOnboarding?: boolean
  allowPendingPasswordless?: boolean
  allowUnauthenticated?: boolean
}> = (props) => {
  const auth = useAuth()
  const location = useLocation()
  const allowed = () =>
    props.allowUnauthenticated ||
    auth.state().authenticated ||
    (props.allowPendingDesktopOnboarding && !!auth.state().pendingDesktopOnboarding) ||
    (props.allowPendingPasswordless && !!auth.state().pendingPasswordless)

  return (
    <Show when={auth.state().initialized} fallback={<AuthLoading message="Restoring your session..." />}>
      <Show
        when={allowed()}
        fallback={
          // OFFLINE at startup is not a signed-out state: a stored session is waiting for the
          // network to come back (auth marks `offline` only when it holds a session it can't reach).
          // Show "reconnecting" and let the retry loop sign the user in when the link returns —
          // never bounce them to /login for a dropped connection.
          <Show
            when={!auth.isOffline()}
            fallback={
              <AuthLoading
                message={authConnectionLoadingMessage(auth.connectionIssue?.() ?? auth.state().connectionIssue)}
              />
            }
          >
            {(() => {
              const path = location.pathname + location.search
              if (path !== "/" && !path.startsWith("/login") && !path.startsWith("/callback")) {
                storeReturnTo(path)
              }
              return null
            })()}
            <Navigate href="/login" />
          </Show>
        }
      >
        {props.children}
      </Show>
    </Show>
  )
}

export const GuestGuard: ParentComponent = (props) => {
  const { state } = useAuth()

  return (
    <Show when={state().initialized} fallback={<AuthLoading message="Restoring your session..." />}>
      <Show when={!state().authenticated} fallback={<Navigate href="/" />}>
        {props.children}
      </Show>
    </Show>
  )
}
